export default {
  "config": {
    "trial": "itc",
    "id": "study-char",
    "type": "table",
    "title": "Study Characteristics of 6 Randomized Controlled Trials of Adults with EDS Associated with OSA<sup>6</sup>",
    "footnotes": [
      "The maximum recommended dose for solriamfetol in the USA PI is <no-break>150 mg.<sup>27</sup></no-break>",
      "<sup>a</sup>Refers to any level of compliance of CPAP, oral pressure therapy, oral appliance, or upper airway stimulator prior to trial start.",
      "<sup>b</sup>Refers to <no-break>≥4 hours</no-break> of CPAP use per night on <no-break>≥70%</no-break> of nights for <no-break>≥2 weeks</no-break> prior to trial start.",
      "<sup>c</sup>Values were derived by censoring original clinical trial data at <no-break>20 minutes</no-break> to generate MWT20.",
      "<sup>d</sup>Values were derived by censoring original clinical trial data at <no-break>30 minutes</no-break> to generate MWT30.",
      "<sup>e</sup>Large standard errors prevented ITC analysis in FOSQ at <no-break>4 weeks.</no-break>",
      "<sup>f</sup>Not considered to be associated with treatment; not analysed via ITC."
    ],
    "useFootnoteTooltip": false
  },
  "data": {
    "trial": "itc",
    "id": "study-char",
    "data-type": "table",
    "title": "Study Characteristics of 6 Randomized Controlled Trials of Adults with EDS Associated with OSA<sup>6</sup>",
    "num-header-rows": 1,
    "num-columns": 7,
    "col-classes": [
      "col-1",
      "schw",
      "black",
      "hirsh",
      "inoue",
      "pack",
      "roth"
    ],
    "row-mergeoption": [
      1,
      10,
      20
    ],
    "footnote-tooltip": "disable",
    "footnote-order": [
      "footnote-note1",
      "footnote-a",
      "footnote-b",
      "footnote-c",
      "footnote-d",
      "footnote-e",
      "footnote-f"
    ],
    "footnote-note1": "The maximum recommended dose for solriamfetol in the USA PI is <no-break>150 mg.<sup>27</sup></no-break>",
    "footnote-a": "<sup>a</sup>Refers to any level of compliance of CPAP, oral pressure therapy, oral appliance, or upper airway stimulator prior to trial start.",
    "footnote-b": "<sup>b</sup>Refers to <no-break>≥4 hours</no-break> of CPAP use per night on <no-break>≥70%</no-break> of nights for <no-break>≥2 weeks</no-break> prior to trial start.",
    "footnote-c": "<sup>c</sup>Values were derived by censoring original clinical trial data at <no-break>20 minutes</no-break> to generate MWT20.",
    "footnote-d": "<sup>d</sup>Values were derived by censoring original clinical trial data at <no-break>30 minutes</no-break> to generate MWT30.",
    "footnote-e": "<sup>e</sup>Large standard errors prevented ITC analysis in FOSQ at <no-break>4 weeks.</no-break>",
    "footnote-f": "<sup>f</sup>Not considered to be associated with treatment; not analysed via ITC.",
    "data": {
      "row-0": [
        {
          "content": null,
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "<b>Schweitzer, 2019<sup>23</sup></b>",
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "<b>Black, 2005<sup>3</sup></b>",
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "<b>Hirshkowitz, 2007<sup>11</sup></b>",
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "<b>Inoue, 2013<sup>12</sup></b>",
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "<b>Pack, 2001<sup>16</sup><br>Dinges, 2003<sup>8</sup></b>",
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "<b>Roth, 2006<sup>20</sup></b>",
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-1": [
        {
          "indent": 0,
          "content": "Placebo-controlled",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "yes",
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "yes",
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "yes",
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "yes",
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "yes",
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "yes",
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-2": [
        {
          "indent": 0,
          "content": "Comparator",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "Solriamfetol 75, 150, or <no-break>300 mg qd</no-break>",
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "Modafinil 200 or <no-break>400 mg qd</no-break>",
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "Armodafinil <no-break>150 mg qd</no-break>",
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "Modafinil <no-break>200 mg qd</no-break>",
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "Modafinil <no-break>400 mg qd</no-break>",
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "Armodafinil 150 or <no-break>250 mg qd</no-break>",
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-3": [
        {
          "indent": 0,
          "content": "Countries",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "Canada, Germany, Netherlands, <no-break>United States</no-break>",
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "<no-break>United Kingdom,</no-break> <no-break>United States</no-break>",
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "Australia, France, Germany, Russia, <no-break>United States</no-break>",
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "Japan",
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "<no-break>United States</no-break>",
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "Canada, <no-break>United States</no-break>",
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-4": [
        {
          "indent": 0,
          "content": "Total sample <no-break>size, N</no-break>",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": 476,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": 309,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": 263,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": 114,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": 157,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": 395,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-5": [
        {
          "indent": 0,
          "content": "Minimum <no-break>age, y</no-break>",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": 18,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": 18,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": 18,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": 20,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": 18,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": 18,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-6": [
        {
          "indent": 0,
          "content": "Minimum ESS score at enrolment",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": 10,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": 10,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": 10,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": 11,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": 10,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": 10,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-7": [
        {
          "indent": 0,
          "content": "CPAP use prior to <no-break>trial, %</no-break>",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "71<sup>a</sup>",
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "100<sup>b</sup>",
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "100<sup>b</sup>",
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "100<sup>b</sup>",
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "100<sup>b</sup>",
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "100<sup>b</sup>",
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-8": [
        {
          "indent": 0,
          "content": "Trial <no-break>duration, wk</no-break>",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": 12,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": 12,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": 12,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": 4,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": 4,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": 12,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-9": [
        {
          "indent": 0,
          "content": "Efficacy outcome time <no-break>points, wk</no-break>",
          "cols": 7,
          "class": "col-1",
          "parentRow": true,
          "parent": 9
        }
      ],
      "row-10": [
        {
          "indent": 1,
          "parent": 9,
          "content": "ESS",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": 4,
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": 4,
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-11": [
        {
          "indent": 1,
          "parent": 9,
          "content": "MWT20",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 12<sup>c</sup>",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": 4,
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-12": [
        {
          "indent": 1,
          "parent": 9,
          "content": "MWT30",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 12<sup>d</sup>",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-13": [
        {
          "indent": 1,
          "parent": 9,
          "content": "MWT40",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 12",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-14": [
        {
          "indent": 1,
          "parent": 9,
          "content": "FOSQ",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": 12,
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "4<sup>e</sup>",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-15": [
        {
          "indent": 1,
          "parent": 9,
          "content": "CGI-C",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": 12,
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": 4,
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-16": [
        {
          "indent": 1,
          "parent": 9,
          "content": "PGI-C",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-17": [
        {
          "indent": 1,
          "parent": 9,
          "content": "EQ-5D",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "4, 8, 12",
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-18": [
        {
          "indent": 1,
          "parent": 9,
          "content": "SF-36v2",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": 8,
          "parent": 9,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "NR",
          "parent": 9,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-19": [
        {
          "indent": 0,
          "content": "TEAEs reported in <no-break>≥5%</no-break> of any </no-break>trial arm</no-break>",
          "cols": 7,
          "class": "col-1",
          "parentRow": true,
          "parent": 19
        }
      ],
      "row-20": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Headache",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-21": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Nausea",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-22": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Decreased appetite",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-23": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Anxiety",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-24": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Nasopharyngitis<sup>f</sup>",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-25": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Diarrhea",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-26": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Dry mouth",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-27": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Insomnia",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-28": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Feeling jittery",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-29": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Sinusitis",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-30": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Irritability",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-31": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Pruritis",
          "cols": 1,
          "class": "col-1"
        },
        {
          "indent": 0,
          "content": "&check;",
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ],
      "row-32": [
        {
          "indent": 1,
          "parent": 19,
          "content": "Others",
          "cols": 1,
          "class": "col-1"
        },
        {
          "content": null,
          "parent": 19,
          "cols": 1,
          "class": "schw"
        },
        {
          "indent": 0,
          "content": "Infection, accidental injury, hypertension, nervousness, dizziness, rhinitis",
          "parent": 19,
          "cols": 1,
          "class": "black"
        },
        {
          "indent": 0,
          "content": "Upper respiratory tract infection, dizziness",
          "parent": 19,
          "cols": 1,
          "class": "hirsh"
        },
        {
          "indent": 0,
          "content": "Upper abdominal pain, palpitation",
          "parent": 19,
          "cols": 1,
          "class": "inoue"
        },
        {
          "indent": 0,
          "content": "Nervousness, rhinitis, dizziness",
          "parent": 19,
          "cols": 1,
          "class": "pack"
        },
        {
          "indent": 0,
          "content": "Dizziness, arthralgia",
          "parent": 19,
          "cols": 1,
          "class": "roth"
        }
      ]
    }
  }
};